<!-- The first-run walkthrough. Factory-owned.

     Every app this factory has built shipped without one, because it was always
     something the app "should" add and never the thing anyone got to. So the
     chassis ships the whole mechanism — the panes, the progress, the skip, the
     seen-it flag — and an app supplies nothing but the cards.

     STOCK IONIC, NO CSS. This used to be a hand-rolled dialog with a translated
     pane track, a swipe handler and ~70 lines of styling — which is exactly the
     kind of bespoke chrome that made a scaffolded app look like neither
     platform. IonModal already knows how to present itself on iOS and on
     Material; IonFooter already knows where a footer goes. Swipe is gone with
     it: Ionic 7 dropped IonSlides in favour of Swiper, and a carousel is not
     worth a dependency in a starter. The buttons do the same job and are
     reachable by keyboard and screen reader, which the swipe never was.

     Renders NOTHING when there are no cards. A half-built onboarding showing
     one blank panel is worse than none, so the empty case is the safe one. -->
<template>
  <IonModal :is-open="open" :backdrop-dismiss="false">
    <IonHeader>
      <IonToolbar>
        <IonTitle>{{ card?.title }}</IonTitle>
        <IonButtons slot="end">
          <IonButton @click="finish">Skip</IonButton>
        </IonButtons>
      </IonToolbar>
    </IonHeader>

    <IonContent class="ion-padding">
      <img v-if="card?.img" :src="card.img" alt="">
      <IonText color="medium">
        <p>{{ card?.body }}</p>
      </IonText>
    </IonContent>

    <IonFooter>
      <IonToolbar>
        <IonButtons slot="start">
          <IonButton :disabled="index === 0" @click="index -= 1">Back</IonButton>
        </IonButtons>
        <IonTitle size="small">{{ index + 1 }} of {{ cards.length }}</IonTitle>
        <IonButtons slot="end">
          <IonButton strong @click="next">
            {{ index >= cards.length - 1 ? 'Get started' : 'Next' }}
          </IonButton>
        </IonButtons>
      </IonToolbar>
    </IonFooter>
  </IonModal>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import {
  IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton,
  IonContent, IonFooter, IonText,
} from '@ionic/vue';
import { prefs } from '../storage.js';

const props = defineProps({
  // [{ title, body?, img? }] — three or four. More than that is a manual.
  cards: { type: Array, default: () => [] },
  // Bump when the cards change materially and everyone should see them again.
  version: { type: [String, Number], default: 1 },
});

const index = ref(0);
const seen = ref(true);
const key = computed(() => `onboarding.v${props.version}`);
const open = computed(() => props.cards.length > 0 && !seen.value);
const card = computed(() => props.cards[index.value] ?? null);

// `prefs` is a Proxy over localStorage — property access, not a method call.
onMounted(() => { seen.value = prefs[key.value] === true; });

function finish() {
  seen.value = true;
  prefs[key.value] = true;
}

function next() {
  if (index.value >= props.cards.length - 1) finish();
  else index.value += 1;
}
</script>
